import {
	type JsonSerializable,
	useAgentContext,
	useCopilotKit,
} from "@copilotkit/react-core/v2";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { APPS_REDEEM_AGENT_NAME } from "../constants";
import {
	type ChatContextType,
	type ChatState,
	initialChatState,
} from "./state";

type ChatActions = {
	setPartialChatState: (state: Partial<ChatState>) => void;
	pushContextMessage: (message: string) => void;
};

const ChatStateContext = createContext<ChatState>({} as ChatState);
const ChatActionsContext = createContext<ChatActions>({} as ChatActions);
const AnalyticsContext = createContext<{ analyticsEnabled: boolean }>({
	analyticsEnabled: false,
});

const useRedeemCopilotState = () => useContext(ChatStateContext);
const useRedeemCopilotActions = () => useContext(ChatActionsContext);
const useRedeemCopilotAnalytics = () => useContext(AnalyticsContext);

export const useRedeemCopilot = (): ChatContextType => {
	const state = useRedeemCopilotState();
	const actions = useRedeemCopilotActions();
	const { analyticsEnabled } = useRedeemCopilotAnalytics();

	return {
		chatState: state,
		...actions,
		analyticsEnabled,
	};
};

export const ContextProvider = ({
	enableAnalytics = false,
	initialChatState: parsedInitialChatState,
	instructions,
	children,
}: {
	enableAnalytics?: boolean;
	initialChatState?: Partial<ChatState>;
	instructions?: string;
	children?: React.ReactNode;
}) => {
	const [chatState, setChatState] = useState<ChatState>({
		...initialChatState,
		...parsedInitialChatState,
	});

	const { chatOpen, userId, ...readableChatState } = chatState;

	useAgentContext({
		description: `The current chat state including username, userEmail,
		appName, appSupportEmail, assistantTitle, locale, language and operational
		instructions.`,
		value: { chatState: readableChatState, instructions } as JsonSerializable,
	});

	useEffect(() => {
		setChatState((prevState) => ({
			...prevState,
			...parsedInitialChatState,
		}));
	}, [parsedInitialChatState]);

	const { copilotkit } = useCopilotKit();

	useEffect(() => {
		const syncAuth = () => {
			const authed = Boolean(
				copilotkit.headers?.Authorization ?? copilotkit.headers?.authorization,
			);
			setChatState((prev) => ({ ...prev, authReady: authed }));
		};

		syncAuth();
		const subscription = copilotkit.subscribe({
			onHeadersChanged: syncAuth,
		});
		return () => subscription.unsubscribe();
	}, [copilotkit]);

	const setPartialChatState = useCallback((state: Partial<ChatState>) => {
		setChatState((prevState) => ({
			...prevState,
			...state,
		}));
	}, []);

	const pushContextMessage = useCallback(
		(message: string) => {
			const agent = copilotkit.getAgent(APPS_REDEEM_AGENT_NAME);
			if (!agent) {
				return;
			}
			agent.addMessage({
				id: globalThis.crypto.randomUUID(),
				role: "system",
				content: message,
			});
			copilotkit.runAgent({ agent }).catch(() => undefined);
		},
		[copilotkit],
	);

	const actionValues = useMemo(
		() => ({
			setPartialChatState,
			pushContextMessage,
		}),
		[setPartialChatState, pushContextMessage],
	);

	const analyticsValue = useMemo(
		() => ({
			analyticsEnabled: enableAnalytics,
		}),
		[enableAnalytics],
	);

	return (
		<AnalyticsContext.Provider value={analyticsValue}>
			<ChatActionsContext.Provider value={actionValues}>
				<ChatStateContext.Provider value={chatState}>
					{children}
				</ChatStateContext.Provider>
			</ChatActionsContext.Provider>
		</AnalyticsContext.Provider>
	);
};
